import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/init-services';
import { DebounceDelays } from '../../shared/debounce-constants';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { isElementInViewport } from '../../utils/is-element-in-viewport';
import { querySelectorAllAsArray } from '../../utils/query-selector-all-as-array';
import { throttle } from '../../utils/throttle';
import { NavigationBehaviour, ScrollBehaviour } from './slider.types';
import { NAVIGATION_BEHAVIOUR_ITEM, NAVIGATION_BEHAVIOUR_PAGE, SCROLL_BEHAVIOUR_SMOOTH } from './slider.constants';

const MAGIC_THRESHOLD_PX = 5;

/**
 * @slot - wb-slider-item elements
 */
@Component({
  tag: 'wb-slider',
  styleUrl: './slider.scss',
  shadow: true,
})
export class SliderComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   * the colour scheme, used for internal wb-dot-navigation
   * @deprecated Use `data-on` attribute instead
   * */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme;

  /** shows an off-grid variant when the components goes over the full width of the page  */
  @Prop({ reflect: true })
  public offGrid: boolean = false;

  /** adds a grid gutter between the slider items */
  @Prop({ reflect: true })
  public gridGutter: boolean = false;

  /** enables scroll snapping */
  @Prop({ reflect: true })
  public scrollSnap: boolean = false;

  /** sets navigation behaviour scrolling by page or item */
  @Prop({ reflect: true })
  public navigationBehaviour: NavigationBehaviour = NAVIGATION_BEHAVIOUR_PAGE;

  /** shows the arrow navigation */
  @Prop({ reflect: true })
  public showArrowNavigation: boolean = false;

  /** shows the dot navigation */
  @Prop({ reflect: true })
  public showDotNavigation: boolean = false;

  /** sets aria-label of next button */
  @Prop()
  public nextButtonAriaLabel!: string;

  /** sets aria-label of previous button */
  @Prop()
  public previousButtonAriaLabel!: string;

  /** interval in miliseconds to auto rotate slides. pass a falsy value to stop auto rotation */
  @Prop({ reflect: true })
  public autoRotateIntervalMs: number = 0;

  /** sets item to jump to without smooth scrolling. can be updated during runtime */
  @Prop({ reflect: true })
  public initialItem: number = 1;

  /** defines position of dot navigation, if rendered */
  @Prop({ reflect: true })
  public dotNavigationPosition: 'below' | 'bottom' = 'below';

  /** defines if dot navigation should have background */
  @Prop({ reflect: true })
  public dotNavigationHasBackground: boolean = false;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** event emitted when current page changes */
  @Event()
  public wbchange!: EventEmitter<number>;

  @State()
  public scrolledToStart: boolean = true;

  @State()
  public scrolledToEnd: boolean = false;

  @State()
  private items: Array<HTMLWbSliderItemElement> = [];

  @State()
  private groupSize: number = 0;

  @State()
  private groupedIndexes: Array<Array<number>> = [];

  @State()
  private numberOfDots: number = 0;

  @State()
  private currentItem: number = 1;

  @State()
  private edges: { right: number; left: number } = { right: 0, left: 0 };

  @State()
  private isLoaded: boolean = false;

  // Use ResizeObserver instead of wbresize event to listen to size changes of the slider itself and not the window
  private resizeObserver: ResizeObserver;

  @Listen('mouseenter')
  public onMouseEnter(): void {
    this.isUserHovering = true;
    this.pauseRotation();
  }

  @Listen('mouseleave')
  public onMouseLeave(): void {
    this.isUserHovering = false;
    this.continueRotation();
  }

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  /** returns the number of the currently active slider item (starts at 1) */
  @Method()
  public async getCurrentItem(): Promise<number> {
    return this.currentItem;
  }

  /** scrolls to the slider item at the given number (starts at 1) */
  @Method()
  public async scrollToItem(item: number, behavior: ScrollBehaviour = SCROLL_BEHAVIOUR_SMOOTH): Promise<void> {
    await this.scrollToIndex(item - 1, behavior);
  }

  /** scrolls to the slider item at the given index (starts at 0) */
  @Method()
  public async scrollToIndex(index: number, behavior: ScrollBehaviour = SCROLL_BEHAVIOUR_SMOOTH): Promise<void> {
    if (!this.items[index]) {
      return;
    }

    const offsetLeft = this.getItemOffsetStartAtIndex(index);
    const offgridAdjustment = this.offGrid ? this.offsetToGridContentAreaOnEachSide : 0;
    const adjustedOffsetLeft = this.isRtl ? offsetLeft + offgridAdjustment : offsetLeft - offgridAdjustment;

    this.scrollerEl.scroll({ left: adjustedOffsetLeft, behavior });
  }

  /** scrolls to the next slider item */
  @Method()
  public async next(): Promise<void> {
    const nextItemIndex = Math.min(this.currentItem, this.items.length - 1);
    await this.scrollToIndex(nextItemIndex);
  }

  /** scrolls to the previous slider item */
  @Method()
  public async previous(): Promise<void> {
    const previousItemIndex = Math.max(this.currentItem - 2, 0);
    await this.scrollToIndex(previousItemIndex);
  }

  /** enforces an update and rerender of the slider */
  @Method()
  public async update(): Promise<void> {
    this.updateArrows();
    this.updateEdges();
    this.updateCurrentItem();
  }

  /** scrolls to the next slider page */
  @Method()
  public async scrollToNextPage(): Promise<void> {
    this.scrollToPage();
  }

  /** scrolls to the previous slider page */
  @Method()
  public async scrollToPreviousPage(): Promise<void> {
    this.scrollToPage(true);
  }

  @Watch('autoRotateIntervalMs')
  public updateAutoRotation(): void {
    this.pauseRotation();
    this.continueRotation();
  }

  @Watch('initialItem')
  public updateInitialItem(): void {
    // Wait for next render cycle, so that child elements can finish rendering before scrolling to initial item
    requestAnimationFrame(() => {
      this.scrollToItem(this.initialItem, 'auto');
    });
  }

  @Watch('items')
  @Watch('isLoaded')
  public updateEdges(): void {
    if (this.isLoaded) {
      this.getEdges();
    }
  }

  @Watch('edges')
  @Watch('groupSize')
  public groupItems(): void {
    if (!this.groupSize) {
      this.getGroupSize();
    }

    if (this.groupSize) {
      this.groupItemsIndexes();
    }
  }

  @Watch('groupedIndexes')
  public updateNumberOfDots(): void {
    this.numberOfDots = Math.ceil(this.items.length / this.groupSize);
  }

  @Watch('numberOfDots')
  public updateCurrentItem(): void {
    const previousCurrentItem = this.currentItem;
    const lastVisibleItem = this.getFirstVisibleItem(this.items) as HTMLWbSliderItemElement;
    const newCurrentItem = this.items.indexOf(lastVisibleItem) + 1;

    if (previousCurrentItem !== newCurrentItem) {
      this.currentItem = newCurrentItem;
      this.wbchange.emit(this.currentItem);
    }
  }

  private TagNames = transformTagNames(this, 'wb-slider', ['wb-icon', 'wb-round-button', 'wb-dot-navigation', 'wb-slider-item']);

  private scrollerEl: HTMLElement;
  private gridWidthEl: HTMLElement;
  private autoRotateInterval: ReturnType<typeof setInterval>;
  private isUserHovering: boolean = false; // Prevents auto rotation when user hovers over the slider

  public connectedCallback(): void {
    this.updateDirection();
  }

  public componentDidLoad(): void {
    this.updateInitialItem();
    this.updateScrolledToStart();
    this.updateAutoRotation();
    this.setLoaded();

    this.resizeObserver = new ResizeObserver(() => {
      this.throttledUpdate();
    });
    this.resizeObserver.observe(this.scrollerEl);
  }

  public componentDidUpdate(): void {
    this.updateScrolledToEnd();
  }

  private throttledUpdate = throttle(() => this.update(), DebounceDelays.Scroll);

  private setLoaded = (): void => {
    // Set isLoaded to ensure the edges are calculated after the items are rendered
    requestAnimationFrame(() => {
      this.isLoaded = true;
    });
  };

  private scrollToPage = (previous: boolean = false): void => {
    const refEl = this.offGrid ? this.gridWidthEl : this.el;
    let scrollDist = this.isRtl ? -refEl.clientWidth : refEl.clientWidth;

    if (previous) {
      scrollDist = -scrollDist;
    }

    this.scrollerEl.scroll({ left: this.scrollerEl.scrollLeft + scrollDist, behavior: 'smooth' });
  };

  private firstFromGroup = (count: number) => {
    const index = count - 1;
    return this.groupedIndexes[index][0];
  };

  private getCurrentPage(): number {
    // If scrolled to the end, return the last page number
    if (this.scrolledToEnd) {
      return this.numberOfDots;
    }

    // If scrolled to the start, return the first page number
    if (this.scrolledToStart) {
      return 1;
    }

    // Find the index of the group that contains the current item
    const groupIndex = this.groupedIndexes.findIndex((group) => group.includes(this.currentItem));

    // If the current item is not part of any group, return -1 (error case)
    if (groupIndex === -1) {
      return -1;
    }

    // If the current item is not the first in its group, adjust the page index
    const isPartialGroup = (this.currentItem - 1) % this.groupSize !== 0;
    return isPartialGroup ? groupIndex + 2 : groupIndex + 1;
  }

  private onDotNavigationChange = (event: CustomEvent<number>): void => {
    event.stopPropagation();
    const item = this.firstFromGroup(event.detail);
    this.scrollToItem(item);
  };

  private onNextButtonClick = (): void => {
    if (this.navigationBehaviour === NAVIGATION_BEHAVIOUR_ITEM) {
      this.next();
    } else {
      this.scrollToNextPage();
    }
  };

  private onPreviousButtonClick = (): void => {
    if (this.navigationBehaviour === NAVIGATION_BEHAVIOUR_ITEM) {
      this.previous();
    } else {
      this.scrollToPreviousPage();
    }
  };

  private pauseRotation(): void {
    if (this.autoRotateInterval) {
      clearInterval(this.autoRotateInterval);
    }
  }

  private continueRotation(): void {
    if (!this.autoRotateIntervalMs || this.isUserHovering) {
      return;
    }

    this.autoRotateInterval = setInterval(this.autoRotateFn, this.autoRotateIntervalMs);
  }

  private autoRotateFn = (): void => {
    if (isElementInViewport(this.el) === false) {
      return;
    }

    if (this.scrolledToEnd) {
      this.scrollToIndex(0);
    } else {
      this.next();
    }
  };

  private get offsetToGridContentAreaOnEachSide(): number {
    return (this.el.clientWidth - this.gridWidthEl.clientWidth) / 2;
  }

  private getItemOffsetStartAtIndex(index: number): number {
    const hostElementRect = this.el.getBoundingClientRect();
    const hostElementStartEdge = this.isRtl ? hostElementRect.x + hostElementRect.width : hostElementRect.x;

    const itemRect = this.items[index].getBoundingClientRect();
    const itemStartEdge = this.isRtl ? itemRect.x + itemRect.width : itemRect.x;

    return this.scrollerEl.scrollLeft + itemStartEdge - hostElementStartEdge;
  }

  private updateArrows = (): void => {
    this.updateScrolledToStart();
    this.updateScrolledToEnd();
  };

  private updateScrolledToStart = (): void => {
    const direction = this.isRtl ? -1 : 1;
    const scrollLeft = this.scrollerEl.scrollLeft * direction;

    this.scrolledToStart = scrollLeft < MAGIC_THRESHOLD_PX;
  };

  private updateScrolledToEnd = (): void => {
    const direction = this.isRtl ? -1 : 1;
    const scrollLeft = this.scrollerEl.scrollLeft * direction;
    const scrollWidth = this.scrollerEl.scrollWidth;

    this.scrolledToEnd = scrollLeft + this.el.clientWidth + MAGIC_THRESHOLD_PX > scrollWidth;
  };

  private getEdges(): void {
    const refEl = this.offGrid ? this.gridWidthEl : this.el;
    const coords = refEl.getBoundingClientRect();

    this.edges = {
      right: coords.x + coords.width,
      left: coords.x,
    };
  }

  private getFirstVisibleItem(items: Array<HTMLElement>): HTMLElement {
    return items
      .concat()
      .reverse()
      .find((item: HTMLElement) => {
        const coords = item.getBoundingClientRect();
        const itemMiddle = coords.x + coords.width / 2 + MAGIC_THRESHOLD_PX;
        const lastArea = this.edges.right - coords.width;
        const initialArea = this.edges.left + coords.width;
        return this.isRtl ? itemMiddle >= lastArea : itemMiddle <= initialArea;
      });
  }

  private updateItems = (): void => {
    this.items = querySelectorAllAsArray<HTMLWbSliderItemElement>(this.el, this.TagNames.WbSliderItem);
  };

  private getGroupSize(): void {
    if (this.navigationBehaviour === NAVIGATION_BEHAVIOUR_ITEM) {
      this.groupSize = 1;
      return;
    }

    let fullyVisibleItems = 0;

    this.items.forEach((item: HTMLElement) => {
      const coords = item.getBoundingClientRect();

      // Count only items that are fully visible
      const itemStart = coords.x + MAGIC_THRESHOLD_PX;
      const itemEnd = coords.x + coords.width - MAGIC_THRESHOLD_PX;
      const isFullyVisible = itemStart >= this.edges.left && itemEnd <= this.edges.right;

      if (isFullyVisible) {
        fullyVisibleItems++;
      }
    });

    this.groupSize = fullyVisibleItems || 1;
  }

  private groupItemsIndexes(): void {
    const itemsIndexes = this.items.map((_, index) => index + 1);
    const maxLength = Math.ceil(itemsIndexes.length / this.groupSize);

    this.groupedIndexes = Array.from({ length: maxLength }, (_, index) => {
      const start = index * this.groupSize;
      const end = start + this.groupSize;

      return itemsIndexes.slice(start, end);
    });
  }

  private bindScrollerRef = (el: HTMLDivElement): void => {
    this.scrollerEl = el;
  };

  private bindGridWidthRef = (el: HTMLDivElement): void => {
    this.gridWidthEl = el;
  };

  public render(): JSX.Element {
    const { WbDotNavigation, WbRoundButton, WbIcon } = this.TagNames;
    const hasLeftArrow = this.showArrowNavigation && !this.scrolledToStart;
    const hasRightArrow = this.showArrowNavigation && !this.scrolledToEnd;
    const hasDotNavigation = this.showDotNavigation && this.numberOfDots > 1;

    return (
      <Host class="wb-slider" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <div class="grid-width" ref={this.bindGridWidthRef} />
        <div class="outer">
          <div class="inner" ref={this.bindScrollerRef} onScroll={this.throttledUpdate} tabindex="0">
            <slot onSlotchange={this.updateItems} />
          </div>
          {hasLeftArrow && (
            <WbRoundButton size="new-l" elevation="s" class="arrow arrow--previous" button-aria-label={this.previousButtonAriaLabel} onClick={this.onPreviousButtonClick}>
              <WbIcon name="chevron-left/24" flip-rtl />
              {this.previousButtonAriaLabel}
            </WbRoundButton>
          )}
          {hasRightArrow && (
            <WbRoundButton size="new-l" elevation="s" class="arrow arrow--next" button-aria-label={this.nextButtonAriaLabel} onClick={this.onNextButtonClick}>
              <WbIcon name="chevron-right/24" flip-rtl />
              {this.nextButtonAriaLabel}
            </WbRoundButton>
          )}
        </div>
        {hasDotNavigation && (
          <WbDotNavigation //
            hasBackground={this.dotNavigationHasBackground}
            currentPage={this.getCurrentPage()}
            onWbchange={this.onDotNavigationChange}
            pages={this.numberOfDots}
            preventDotClickChange
          />
        )}
      </Host>
    );
  }
}
